/**
 * address-parser.ts
 *
 * Parse a single address string (as returned by VAT validation) into
 * address / zip / city, applying country-specific rules and converting
 * uppercase imports into proper case.
 *
 * Usage:
 *   const result = parseAddress('199 RINGINGLOW ROAD SHEFFIELD S11 7PT GB', 'GB');
 */

export interface ParsedAddress {
  address: string;
  zip: string;
  city: string;
  countryCode: string | null;
  parser: string;
  confidence: 'high' | 'medium' | 'low';
  requiresManualReview: boolean;
  rawAddress: string;
  reason?: string;
  state?: string;
  province?: string;
}

type CountryParser = (rawAddress: string, countryCode: string) => ParsedAddress;

/**
 * Parse a raw address string using the given ISO2 country code.
 */
export function parseAddress(rawAddress: string, iso2: string | null | undefined): ParsedAddress {
  const countryCode = normalizeIso2(iso2);

  if (!countryCode) {
    return fallbackParser(rawAddress, 'Unknown ISO2 country code');
  }

  const parser = PARSERS[countryCode] || fallbackParserByCountry;
  return formatParsedResult(parser(rawAddress, countryCode));
}

/**
 * Country parser registry.
 */
const PARSERS: Record<string, CountryParser> = {
  // 4-digit postal codes
  AT: parseFourDigitPostcodeCountry,
  BE: parseFourDigitPostcodeCountry,
  CH: parseFourDigitPostcodeCountry,
  DK: parseFourDigitPostcodeCountry,
  LU: parseFourDigitPostcodeCountry,
  NO: parseFourDigitPostcodeCountry,

  // 5-digit postal codes
  DE: parseFiveDigitPostcodeCountry,
  EE: parseFiveDigitPostcodeCountry,
  ES: parseFiveDigitPostcodeCountry,
  FI: parseFiveDigitPostcodeCountry,
  FR: parseFiveDigitPostcodeCountry,
  IT: parseFiveDigitPostcodeCountry,
  LT: parseFiveDigitPostcodeCountry,

  // Country-specific formats
  CA: parseCanada,
  GB: parseUnitedKingdom,
  IE: parseIreland,
  LV: parseLatvia,
  NL: parseNetherlands,
  PL: parsePoland,
  PT: parsePortugal,
  SE: parseSweden,
  US: parseUnitedStates
};

function normalizeIso2(value: string | null | undefined): string | null {
  if (!value) return null;
  const normalized = String(value).trim().toUpperCase();
  return /^[A-Z]{2}$/.test(normalized) ? normalized : null;
}

function normalizeAddress(input: string | null | undefined): string {
  return String(input || '')
    .replace(/\u00A0/g, ' ')
    .replace(/\r?\n/g, ' ')
    .replace(/\t/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function cleanPart(value: string | null | undefined): string {
  return normalizeAddress(value);
}

function escapeRegExp(value: string): string {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function removeTrailingCountryCode(text: string, countryCode: string): string {
  const aliases: Record<string, string[]> = {
    GB: ['GB', 'UK'],
    US: ['US', 'USA']
  };

  const codes = aliases[countryCode] || [countryCode];
  const pattern = codes.map(escapeRegExp).join('|');

  return text.replace(new RegExp(`\\s+(?:${pattern})$`, 'i'), '').trim();
}

interface SuccessArgs {
  address: string;
  zip: string;
  city: string;
  countryCode: string;
  parser: string;
  confidence?: 'high' | 'medium' | 'low';
  rawAddress: string;
  extra?: Partial<ParsedAddress>;
}

function successResult(args: SuccessArgs): ParsedAddress {
  const confidence = args.confidence || 'high';
  return {
    address: cleanPart(args.address),
    zip: cleanPart(args.zip),
    city: cleanPart(args.city),
    countryCode: args.countryCode,
    parser: args.parser,
    confidence,
    requiresManualReview: confidence === 'low',
    rawAddress: args.rawAddress,
    ...args.extra
  };
}

function failedParse(rawAddress: string, countryCode: string | null, reason: string): ParsedAddress {
  return {
    address: normalizeAddress(rawAddress),
    zip: '',
    city: '',
    countryCode: countryCode || null,
    parser: countryCode || 'unknown',
    confidence: 'low',
    requiresManualReview: true,
    reason,
    rawAddress
  };
}

function fallbackParser(rawAddress: string, reason: string, countryCode: string | null = null): ParsedAddress {
  return {
    address: normalizeAddress(rawAddress),
    zip: '',
    city: '',
    countryCode,
    parser: 'fallback',
    confidence: 'low',
    requiresManualReview: true,
    reason,
    rawAddress
  };
}

function fallbackParserByCountry(rawAddress: string, countryCode: string): ParsedAddress {
  return fallbackParser(rawAddress, `No country-specific parser configured for ${countryCode}`, countryCode);
}

/**
 * Denmark, Norway, Austria, Belgium, Switzerland, Luxembourg
 * Format: Street address 1234 City
 */
function parseFourDigitPostcodeCountry(rawAddress: string, countryCode: string): ParsedAddress {
  let text = normalizeAddress(rawAddress);
  text = removeTrailingCountryCode(text, countryCode);

  const match = text.match(/^(.*?)\s+(\d{4})\s+(.+)$/i);
  if (!match) {
    return failedParse(rawAddress, countryCode, 'Could not find 4-digit postcode');
  }

  return successResult({
    address: match[1],
    zip: match[2],
    city: match[3],
    countryCode,
    parser: countryCode,
    rawAddress
  });
}

/**
 * Sweden
 * Format: Street address 123 45 City
 */
function parseSweden(rawAddress: string, countryCode = 'SE'): ParsedAddress {
  let text = normalizeAddress(rawAddress);
  text = removeTrailingCountryCode(text, countryCode);

  const match = text.match(/^(.*?)\s+(\d{3}\s?\d{2})\s+(.+)$/i);
  if (!match) {
    return failedParse(rawAddress, countryCode, 'Could not find Swedish postcode');
  }

  return successResult({
    address: match[1],
    zip: normalizeSwedishPostcode(match[2]),
    city: match[3],
    countryCode,
    parser: countryCode,
    rawAddress
  });
}

function normalizeSwedishPostcode(zip: string): string {
  const clean = String(zip).replace(/\s+/g, '');
  return /^\d{5}$/.test(clean) ? `${clean.slice(0, 3)} ${clean.slice(3)}` : String(zip);
}

/**
 * Estonia, Finland, Germany, France, Spain, Italy, Lithuania
 * Format: Street address 12345 City
 */
function parseFiveDigitPostcodeCountry(rawAddress: string, countryCode: string): ParsedAddress {
  let text = normalizeAddress(rawAddress);
  text = removeTrailingCountryCode(text, countryCode);

  const match = text.match(/^(.*?)\s+(\d{5})\s+(.+)$/i);
  if (!match) {
    return failedParse(rawAddress, countryCode, 'Could not find 5-digit postcode');
  }

  return successResult({
    address: match[1],
    zip: match[2],
    city: match[3],
    countryCode,
    parser: countryCode,
    rawAddress
  });
}

/**
 * Latvia
 * Format: Street address LV-1234 City or Street address 1234 City
 */
function parseLatvia(rawAddress: string, countryCode = 'LV'): ParsedAddress {
  let text = normalizeAddress(rawAddress);
  text = removeTrailingCountryCode(text, countryCode);

  const match = text.match(/^(.*?)\s+(LV-?\d{4}|\d{4})\s+(.+)$/i);
  if (!match) {
    return failedParse(rawAddress, countryCode, 'Could not find Latvian postcode');
  }

  return successResult({
    address: match[1],
    zip: match[2],
    city: match[3],
    countryCode,
    parser: countryCode,
    rawAddress
  });
}

/**
 * Poland
 * Format: Street address 12-345 City
 */
function parsePoland(rawAddress: string, countryCode = 'PL'): ParsedAddress {
  let text = normalizeAddress(rawAddress);
  text = removeTrailingCountryCode(text, countryCode);

  const match = text.match(/^(.*?)\s+(\d{2}-?\d{3})\s+(.+)$/i);
  if (!match) {
    return failedParse(rawAddress, countryCode, 'Could not find Polish postcode');
  }

  return successResult({
    address: match[1],
    zip: match[2],
    city: match[3],
    countryCode,
    parser: countryCode,
    rawAddress
  });
}

/**
 * Portugal
 * Format: Street address 1234-567 City
 */
function parsePortugal(rawAddress: string, countryCode = 'PT'): ParsedAddress {
  let text = normalizeAddress(rawAddress);
  text = removeTrailingCountryCode(text, countryCode);

  const match = text.match(/^(.*?)\s+(\d{4}-?\d{3})\s+(.+)$/i);
  if (!match) {
    return failedParse(rawAddress, countryCode, 'Could not find Portuguese postcode');
  }

  return successResult({
    address: match[1],
    zip: match[2],
    city: match[3],
    countryCode,
    parser: countryCode,
    rawAddress
  });
}

/**
 * Netherlands
 * Format: Street address 1234 AB City
 */
function parseNetherlands(rawAddress: string, countryCode = 'NL'): ParsedAddress {
  let text = normalizeAddress(rawAddress);
  text = removeTrailingCountryCode(text, countryCode);

  const match = text.match(/^(.*?)\s+(\d{4}\s?[A-Z]{2})\s+(.+)$/i);
  if (!match) {
    return failedParse(rawAddress, countryCode, 'Could not find Dutch postcode');
  }

  return successResult({
    address: match[1],
    zip: match[2],
    city: match[3],
    countryCode,
    parser: countryCode,
    rawAddress
  });
}

/**
 * United Kingdom
 * Typical imported format: 199 RINGINGLOW ROAD SHEFFIELD S11 7PT GB
 */
function parseUnitedKingdom(rawAddress: string, countryCode = 'GB'): ParsedAddress {
  let text = normalizeAddress(rawAddress);
  text = removeTrailingCountryCode(text, countryCode);

  const postcodeMatch = text.match(/\b([A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2})\b$/i);
  if (!postcodeMatch) {
    return failedParse(rawAddress, countryCode, 'Could not find UK postcode');
  }

  const zip = normalizeUkPostcode(postcodeMatch[1]);
  const beforeZip = text.slice(0, postcodeMatch.index).trim();
  const split = splitAddressAndCityByStreetSuffix(beforeZip, 'GB');

  return successResult({
    address: split.address,
    zip,
    city: split.city,
    countryCode,
    parser: countryCode,
    confidence: split.confidence,
    rawAddress
  });
}

function normalizeUkPostcode(zip: string): string {
  const clean = String(zip).toUpperCase().replace(/\s+/g, '');
  return /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(clean)
    ? `${clean.slice(0, -3)} ${clean.slice(-3)}`
    : String(zip).toUpperCase();
}

/**
 * Ireland
 * Eircode format: A65 F4E2 etc.
 */
function parseIreland(rawAddress: string, countryCode = 'IE'): ParsedAddress {
  let text = normalizeAddress(rawAddress);
  text = removeTrailingCountryCode(text, countryCode);

  const match = text.match(/\b([A-Z]\d{2}\s?[A-Z0-9]{4})\b$/i);
  if (!match) {
    return failedParse(rawAddress, countryCode, 'Could not find Irish Eircode');
  }

  const zip = normalizeIrishEircode(match[1]);
  const beforeZip = text.slice(0, match.index).trim();
  const split = splitAddressAndCityByStreetSuffix(beforeZip, 'IE');

  return successResult({
    address: split.address,
    zip,
    city: split.city,
    countryCode,
    parser: countryCode,
    confidence: split.confidence,
    rawAddress
  });
}

function normalizeIrishEircode(zip: string): string {
  const clean = String(zip).toUpperCase().replace(/\s+/g, '');
  return /^[A-Z]\d{2}[A-Z0-9]{4}$/.test(clean)
    ? `${clean.slice(0, 3)} ${clean.slice(3)}`
    : String(zip).toUpperCase();
}

/**
 * Canada
 * Typical format: Street address City Province A1A 1A1 CA
 */
function parseCanada(rawAddress: string, countryCode = 'CA'): ParsedAddress {
  let text = normalizeAddress(rawAddress);
  text = removeTrailingCountryCode(text, countryCode);

  const match = text.match(/^(.*?)\s+([A-Z]{2})\s+([A-Z]\d[A-Z]\s?\d[A-Z]\d)$/i);
  if (!match) {
    return failedParse(rawAddress, countryCode, 'Could not find Canadian province and postal code');
  }

  const province = match[2].toUpperCase();
  const zip = normalizeCanadianPostcode(match[3]);
  const split = splitAddressAndCityByStreetSuffix(match[1].trim(), 'CA');

  return successResult({
    address: split.address,
    zip,
    city: split.city,
    countryCode,
    parser: countryCode,
    confidence: split.confidence,
    rawAddress,
    extra: { province }
  });
}

function normalizeCanadianPostcode(zip: string): string {
  const clean = String(zip).toUpperCase().replace(/\s+/g, '');
  return /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(clean)
    ? `${clean.slice(0, 3)} ${clean.slice(3)}`
    : String(zip).toUpperCase();
}

/**
 * United States
 * Typical format: Street address City ST 12345 US
 */
function parseUnitedStates(rawAddress: string, countryCode = 'US'): ParsedAddress {
  let text = normalizeAddress(rawAddress);
  text = removeTrailingCountryCode(text, countryCode);

  const match = text.match(/^(.*?)\s+([A-Z]{2})\s+(\d{5}(?:-?\d{4})?)$/i);
  if (!match) {
    return failedParse(rawAddress, countryCode, 'Could not find US state and ZIP');
  }

  const state = match[2].toUpperCase();
  const zip = normalizeUsZip(match[3]);
  const split = splitAddressAndCityByStreetSuffix(match[1].trim(), 'US');

  return successResult({
    address: split.address,
    zip,
    city: split.city,
    countryCode,
    parser: countryCode,
    confidence: split.confidence,
    rawAddress,
    extra: { state }
  });
}

function normalizeUsZip(zip: string): string {
  const clean = String(zip).replace(/\s+/g, '');
  if (/^\d{9}$/.test(clean)) {
    return `${clean.slice(0, 5)}-${clean.slice(5)}`;
  }
  return clean;
}

interface AddressCitySplit {
  address: string;
  city: string;
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Address/city split heuristic for countries where city comes before postcode/state.
 */
function splitAddressAndCityByStreetSuffix(text: string, countryCode: string): AddressCitySplit {
  const tokens = normalizeAddress(text).split(/\s+/).filter(Boolean);

  if (tokens.length <= 1) {
    return { address: text, city: '', confidence: 'low' };
  }

  const streetSuffixes = getStreetSuffixes(countryCode);

  for (let i = tokens.length - 1; i >= 0; i--) {
    const token = tokens[i].replace(/[.,]/g, '').toUpperCase();

    if (streetSuffixes.has(token) && i < tokens.length - 1) {
      return {
        address: tokens.slice(0, i + 1).join(' '),
        city: tokens.slice(i + 1).join(' '),
        confidence: 'high'
      };
    }
  }

  return {
    address: tokens.slice(0, -1).join(' '),
    city: tokens.slice(-1).join(' '),
    confidence: 'medium'
  };
}

function getStreetSuffixes(countryCode: string): Set<string> {
  const commonEnglish = [
    'ROAD', 'RD', 'STREET', 'ST', 'AVENUE', 'AVE', 'LANE', 'LN',
    'DRIVE', 'DR', 'CLOSE', 'CL', 'COURT', 'CT', 'PLACE', 'PL',
    'GARDENS', 'GDN', 'WAY', 'CRESCENT', 'CRES', 'TERRACE', 'TER',
    'SQUARE', 'SQ', 'HILL', 'ROW', 'MEWS', 'PARK', 'BLVD', 'BOULEVARD',
    'PKWY', 'PARKWAY', 'HWY', 'HIGHWAY', 'CIR', 'CIRCLE'
  ];

  const byCountry: Record<string, string[]> = {
    GB: commonEnglish,
    IE: commonEnglish,
    US: commonEnglish,
    CA: commonEnglish
  };

  return new Set(byCountry[countryCode] || commonEnglish);
}

/**
 * Formatting layer. Applied after parsing.
 */
function formatParsedResult(result: ParsedAddress): ParsedAddress {
  const countryCode = normalizeIso2(result.countryCode) || result.countryCode;

  if (result.requiresManualReview) {
    return {
      ...result,
      address: cleanPart(result.address),
      zip: formatPostalCode(result.zip, countryCode),
      city: cleanPart(result.city),
      countryCode
    };
  }

  return {
    ...result,
    address: formatAddressText(result.address, countryCode),
    zip: formatPostalCode(result.zip, countryCode),
    city: formatCityText(result.city, countryCode),
    countryCode,
    state: result.state ? String(result.state).toUpperCase() : result.state,
    province: result.province ? String(result.province).toUpperCase() : result.province
  };
}

function formatAddressText(value: string, countryCode: string | null): string {
  const text = cleanPart(value);
  return text ? smartTitleCase(text, countryCode) : '';
}

function formatCityText(value: string, countryCode: string | null): string {
  const text = cleanPart(value);
  return text ? smartTitleCase(text, countryCode) : '';
}

function formatPostalCode(zip: string, countryCode: string | null): string {
  const value = cleanPart(zip);
  if (!value) return '';

  const clean = value.replace(/\s+/g, '').toUpperCase();

  switch (countryCode) {
    case 'SE':
      return /^\d{5}$/.test(clean) ? `${clean.slice(0, 3)} ${clean.slice(3)}` : value;

    case 'DK':
    case 'NO':
    case 'AT':
    case 'BE':
    case 'CH':
    case 'LU':
      return /^\d{4}$/.test(clean) ? clean : value;

    case 'DE':
    case 'EE':
    case 'ES':
    case 'FI':
    case 'FR':
    case 'IT':
    case 'LT':
      return /^\d{5}$/.test(clean) ? clean : value;

    case 'GB':
      return normalizeUkPostcode(value);

    case 'IE':
      return normalizeIrishEircode(value);

    case 'NL':
      return /^\d{4}[A-Z]{2}$/.test(clean) ? `${clean.slice(0, 4)} ${clean.slice(4)}` : value.toUpperCase();

    case 'LV':
      if (/^\d{4}$/.test(clean)) return `LV-${clean}`;
      if (/^LV-?\d{4}$/.test(clean)) return clean.replace(/^LV-?/, 'LV-');
      return value.toUpperCase();

    case 'PL':
      if (/^\d{5}$/.test(clean)) return `${clean.slice(0, 2)}-${clean.slice(2)}`;
      if (/^\d{2}-\d{3}$/.test(value)) return value;
      return value;

    case 'PT':
      if (/^\d{7}$/.test(clean)) return `${clean.slice(0, 4)}-${clean.slice(4)}`;
      if (/^\d{4}-\d{3}$/.test(value)) return value;
      return value;

    case 'US':
      return normalizeUsZip(value);

    case 'CA':
      return normalizeCanadianPostcode(value);

    default:
      return value;
  }
}

/**
 * Smart title/proper case for imported all-uppercase Latin-script address data.
 */
function smartTitleCase(value: string, countryCode: string | null = null): string {
  return String(value || '')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(word => formatWord(word, countryCode))
    .join(' ');
}

function formatWord(word: string, countryCode: string | null): string {
  if (!word) return '';

  const original = word.trim();
  const upper = original.toUpperCase();

  if (/^C\/O$/i.test(original) || /^CO$/i.test(original)) return 'C/O';
  if (/^P\.?O\.?$/i.test(original)) return 'PO';
  if (/^BOX$/i.test(original)) return 'Box';

  const exactUpperKeep = new Set([
    'AB', 'AS', 'A/S', 'BV', 'NV', 'LLC', 'PLC', 'SA', 'SAS', 'SL', 'SRL', 'OY'
  ]);

  const exactMixedKeep: Record<string, string> = {
    APS: 'ApS',
    GMBH: 'GmbH',
    LTD: 'Ltd',
    LIMITED: 'Limited',
    INC: 'Inc'
  };

  if (exactUpperKeep.has(upper)) return upper;
  if (exactMixedKeep[upper]) return exactMixedKeep[upper];

  const addressAbbreviations: Record<string, string> = {
    LGH: 'Lgh',
    APT: 'Apt',
    FL: 'Fl',
    FLOOR: 'Floor',
    UNIT: 'Unit',
    SUITE: 'Suite',
    STE: 'Ste'
  };

  if (addressAbbreviations[upper]) return addressAbbreviations[upper];

  if (countryCode && ['DK', 'NO', 'SE'].includes(countryCode)) {
    if (upper === 'TH') return 'th';
    if (upper === 'TV') return 'tv';
    if (upper === 'MF') return 'mf';
  }

  if (/^\d+[a-z]$/i.test(original)) {
    return original.replace(/[a-z]$/i, char => char.toUpperCase());
  }

  if (/^\d+(ST|ND|RD|TH)$/i.test(original)) {
    return original.toLowerCase();
  }

  if (/^[a-z]$/i.test(original)) {
    return original.toUpperCase();
  }

  if (/^(I|II|III|IV|V|VI|VII|VIII|IX|X)$/i.test(original)) {
    return upper;
  }

  return original
    .toLocaleLowerCase()
    .replace(/(^|[-'’/])(\p{L})/gu, (_, prefix: string, char: string) => prefix + char.toLocaleUpperCase());
}
